import React from "react";
import { Box, TextField, Typography } from "@mui/material";
import { DIFFICULTY_COLORS, TEST_DIFFICULTIES } from "../../api/assessmentTests";

const FIELD_SX = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const isBlank = (v) => v === "" || v === null || v === undefined;
const isInvalidCount = (c) => !isBlank(c) && !(Number.isInteger(Number(c)) && Number(c) >= 0);

// Body of the "Difficulty Distribution" card (the form renders the card and its header).
// value: { [difficulty]: count } with string or number counts.
function DifficultySplitCard({ totalQuestions, value, onChange, disabled }) {
  const counts = value || {};
  const total = Number(totalQuestions) || 0;
  const numberOf = (d) => {
    const c = counts[d.value];
    return isInvalidCount(c) ? 0 : Number(c) || 0;
  };
  const sum = TEST_DIFFICULTIES.reduce((s, d) => s + numberOf(d), 0);
  const anyInvalid = TEST_DIFFICULTIES.some((d) => isInvalidCount(counts[d.value]));
  const balanced = total > 0 && sum === total && !anyInvalid;

  // Segment widths are capped so the bar never overflows when the counts exceed the total.
  let used = 0;
  const segments = TEST_DIFFICULTIES.map((d) => {
    const n = total > 0 ? Math.min(numberOf(d), Math.max(total - used, 0)) : 0;
    used += n;
    return { ...d, width: total > 0 ? (n / total) * 100 : 0 };
  });
  const remainderWidth = total > 0 ? Math.max(100 - (used / total) * 100, 0) : 100;

  let hint = "";
  if (sum < total) hint = ` - ${total - sum} unassigned`;
  else if (sum > total) hint = ` - ${sum - total} over`;
  else if (anyInvalid) hint = " - fix the highlighted counts";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {TEST_DIFFICULTIES.map((d) => {
        const raw = counts[d.value];
        const invalid = isInvalidCount(raw);
        const share = total > 0 && !invalid ? `${Math.round((numberOf(d) / total) * 100)}%` : "";
        return (
          <Box key={d.value} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: 130, flexShrink: 0, height: 40 }}>
              <Box
                sx={{ width: 10, height: 10, borderRadius: "50%", flexShrink: 0, backgroundColor: DIFFICULTY_COLORS[d.value] }}
              />
              <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: DIFFICULTY_COLORS[d.value] }}>
                {d.label}
              </Typography>
            </Box>
            <TextField
              size="small"
              type="number"
              disabled={disabled}
              value={raw ?? ""}
              onChange={(e) => onChange({ ...counts, [d.value]: e.target.value })}
              error={invalid}
              helperText={invalid ? "Whole number 0 or more" : undefined}
              inputProps={{ min: 0, step: 1, "aria-label": `${d.label} questions` }}
              sx={{ flex: 1, minWidth: 0, ...FIELD_SX }}
            />
            <Typography
              sx={{ width: 44, textAlign: "right", lineHeight: "40px", fontSize: "0.8rem", color: "text.secondary" }}
            >
              {share}
            </Typography>
          </Box>
        );
      })}

      <Box
        data-testid="difficulty-bar"
        role="img"
        aria-label={`Difficulty mix: ${TEST_DIFFICULTIES.map((d) => `${d.label} ${numberOf(d)}`).join(", ")}`}
        sx={{ display: "flex", height: 10, borderRadius: 999, overflow: "hidden", backgroundColor: "#e6e9ef" }}
      >
        {segments.map((s) => (
          <Box key={s.value} sx={{ width: `${s.width}%`, backgroundColor: DIFFICULTY_COLORS[s.value] }} />
        ))}
        <Box sx={{ width: `${remainderWidth}%`, backgroundColor: "#e6e9ef" }} />
      </Box>

      <Typography role="status" variant="caption" sx={{ fontWeight: 600, color: balanced ? "#2e7d32" : "#b26a00" }}>
        Difficulty total: {sum} / {total} questions{hint}
      </Typography>
    </Box>
  );
}

export default DifficultySplitCard;
